import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import {
  createMRTColumnHelper,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_Updater,
  type MRT_VisibilityState as VisibilityState,
} from "material-react-table";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";
import FeeScheduleCategory from "../fee-schedule-category";
import { feeScheduleCategoryTypeLabel } from "_shared/fee-schedules/categories/types/fee-schedule-category-type";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import {
  FeeScheduleTableCellNoValue,
  FeeScheduleTableCellText,
  feeScheduleTableCellStyles,
} from "./fee-schedule-table-cell";
import FeeScheduleTeamMemberCell from "./fee-schedule-team-member-cell";
import {
  departedRateCellStyles,
  departureOfRate,
  indexDeparturesByUserId,
  type FeeScheduleTeamMemberDeparture,
} from "./fee-schedule-departed-rate";
import { paneStretchTable } from "_shared/_views/tables/table-pane-stretch";
import type { TableColumnKind } from "_shared/_views/tables/table-column-fit";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import { feeScheduleColumnWidth } from "work/fee-schedule-templates/_views/tables/fee-schedule-table-columns";
import {
  proposalFeeScheduleRestrictedTeamMemberText,
  proposalFeeScheduleTeamMemberFallbackText,
} from "work/proposals/negotiation/proposal-fee-schedule-aspects";

const ToolbarButtons = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "nowrap",
  // On a phone the last icon's glyph, not its button, meets the right-hand edge the alert above
  // ends on, as in the shared collection toolbar (#5907).
  [theme.breakpoints.down("sm")]: { marginRight: theme.spacing(-1) },
}));
/**
 * The search at the start of the row and the view buttons at its far end, at every width — the shape
 * every other collection's toolbar has. A wide screen used to put the two together in MRT's
 * internal-actions slot instead, and with the custom-actions slot beside it empty, that one group sat
 * at the row's start with the buttons half the table short of its right-hand edge.
 */
const ToolbarRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: theme.spacing(1),
  minWidth: 0,
  width: "100%",
}));

type FeeScheduleCategoriesReadonlyProps = {
  className?: string;
  categories: Array<FeeScheduleCategory> | Array<FeeScheduleCategoryMaskedCurrency>;
  /**
   * The people who have left the team this schedule belongs to. A rate still naming one of them is
   * drawn as departed: its whole row struck through, and how they left beside the name (#6096).
   */
  teamMemberDepartures?: readonly FeeScheduleTeamMemberDeparture[];
};

/**
 * One row of the table: the category, and who holds its rate in words.
 *
 * The words are part of the row rather than computed by the column's accessor because TanStack
 * caches each row's accessor values and rebuilds its row model only when `data` changes identity.
 * The directory names a team member after the first render, so an accessor would go on answering
 * search and sorting with the "Team member" stand-in the row was first built with (#5228). Built here,
 * the rows are rebuilt when the names arrive, because the names are what they are built from.
 */
type ReadonlyFeeScheduleRow = {
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;
  teamMemberText: string;
  /** How the rate's person left the team, when they have. */
  departure: FeeScheduleTeamMemberDeparture | undefined;
};

const columnHelper = createMRTColumnHelper<ReadonlyFeeScheduleRow>();

/**
 * What each column holds, which is how the shared list-table fit sizes it (#6708). The widths used to
 * be stated here, a heading's label and its padding apiece (#6524); they are measured now.
 */
const READONLY_FEE_SCHEDULE_COLUMN_KINDS: Readonly<Record<string, TableColumnKind>> = {
  name: "name",
  description: "text",
  fixedFee: "status",
  fee: "money",
  unit: "text",
  teamMember: "person",
  type: "status",
};

/**
 * What MRT hands a column's `Cell`. `renderedCellValue` is the accessor's value with the search term
 * marked, as MRT marks the cells it draws itself. Each cell below shows exactly its accessor's value,
 * so it draws that, and the table marks what its search found in every column (#6590). The plain
 * value stays the clipped cell's tooltip.
 */
type ReadonlyFeeScheduleCellProps = {
  row: { original: ReadonlyFeeScheduleRow };
  renderedCellValue: ReactNode;
};

type CategoryCellProps = {
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;
  renderedValue: ReactNode;
};

function CategoryNameCell({ category, renderedValue }: Readonly<CategoryCellProps>) {
  const value = getCategoryNameValue(category);
  return <FeeScheduleTableCellText emphasized title={value}>{renderedValue}</FeeScheduleTableCellText>;
}

function renderCategoryNameCell({ row, renderedCellValue }: ReadonlyFeeScheduleCellProps) {
  return <CategoryNameCell category={row.original.category} renderedValue={renderedCellValue} />;
}

function CategoryDescriptionCell({ category, renderedValue }: Readonly<CategoryCellProps>) {
  const value = getCategoryDescriptionValue(category);
  return <FeeScheduleTableCellText title={value}>{renderedValue}</FeeScheduleTableCellText>;
}

function renderCategoryDescriptionCell({ row, renderedCellValue }: ReadonlyFeeScheduleCellProps) {
  return <CategoryDescriptionCell category={row.original.category} renderedValue={renderedCellValue} />;
}

type CategoryFeeCellProps = CategoryCellProps & {
  viewingAsVendor?: boolean;
};

function CategoryFeeCell({ category, renderedValue, viewingAsVendor }: Readonly<CategoryFeeCellProps>) {
  const feeValue = getCategoryFeeValue(category, viewingAsVendor);
  return feeValue.startsWith("Deferred to ") ? (
    <FeeScheduleTableCellText color="warning" title={feeValue}>{renderedValue}</FeeScheduleTableCellText>
  ) : (
    <FeeScheduleTableCellText title={feeValue}>{renderedValue}</FeeScheduleTableCellText>
  );
}

function renderCategoryFeeCell({ row, renderedCellValue }: ReadonlyFeeScheduleCellProps, viewingAsVendor?: boolean) {
  return (
    <CategoryFeeCell category={row.original.category} renderedValue={renderedCellValue} viewingAsVendor={viewingAsVendor} />
  );
}

function CategoryUnitCell({ category, renderedValue }: Readonly<CategoryCellProps>) {
  const unitValue = getCategoryUnitValue(category);
  return unitValue ? (
    <FeeScheduleTableCellText title={unitValue}>{renderedValue}</FeeScheduleTableCellText>
  ) : (
    <FeeScheduleTableCellNoValue />
  );
}

function renderCategoryUnitCell({ row, renderedCellValue }: ReadonlyFeeScheduleCellProps) {
  return <CategoryUnitCell category={row.original.category} renderedValue={renderedCellValue} />;
}

function CategoryTypeCell({ category, renderedValue }: Readonly<CategoryCellProps>) {
  return (
    <FeeScheduleTableCellText title={getCategoryTypeValue(category)}>{renderedValue}</FeeScheduleTableCellText>
  );
}

function renderCategoryTypeCell({ row, renderedCellValue }: ReadonlyFeeScheduleCellProps) {
  return <CategoryTypeCell category={row.original.category} renderedValue={renderedCellValue} />;
}

export default function ReadonlyFeeScheduleCategoriesTable(props: Readonly<FeeScheduleCategoriesReadonlyProps>) {
  const { className, categories, teamMemberDepartures } = props;

  const session = useSession();
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  /** Columns the reader hid from the Show/Hide columns menu, whose width goes to the ones still drawn. */
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const teamMemberUserIds = useMemo(
    () => new Set(categories.flatMap((category) => category.teamMember?.userId.value ?? [])),
    [categories],
  );
  const teamMemberIndividualsQuery = useGetIndividualsByUserIdsQuery(
    teamMemberUserIds.size > 0 ? { userIds: [...teamMemberUserIds] } : skipToken,
  );
  const teamMemberIndividuals = useMemo(
    () => new Map(
      (Individual.fromJSONArray(teamMemberIndividualsQuery.data, { allowEmpty: true }) ?? []).map((individual) => [
        individual.userId.value,
        individual,
      ]),
    ),
    [teamMemberIndividualsQuery.data],
  );
  const departuresByUserId = useMemo(() => indexDeparturesByUserId(teamMemberDepartures), [teamMemberDepartures]);
  // A deliberate stability guarantee, not defensive memoization: `data` must keep its identity until
  // the categories or the names change, or TanStack rebuilds every row on every render.
  const rows = useMemo<ReadonlyFeeScheduleRow[]>(
    () => categories.map((category) => {
      const departure = departureOfRate(category, departuresByUserId);
      return {
        category,
        teamMemberText: describeTeamMember(category, teamMemberIndividuals, departure),
        departure,
      };
    }),
    [categories, departuresByUserId, teamMemberIndividuals],
  );

  const columns = useMemo(() => [
    columnHelper.accessor((row) => getCategoryNameValue(row.category), {
      header: "Name",
      id: "name",
      meta: { kind: READONLY_FEE_SCHEDULE_COLUMN_KINDS.name },
      enableSorting: true,
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: "includesString",
      Cell: renderCategoryNameCell,
    }),
    columnHelper.accessor((row) => getCategoryDescriptionValue(row.category), {
      header: "Description",
      id: "description",
      meta: {
        kind: READONLY_FEE_SCHEDULE_COLUMN_KINDS.description,
        // A description is capped rather than handed the slack (#5111).
        width: { maximum: feeScheduleColumnWidth("description").maximum },
      },
      enableSorting: true,
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: "includesString",
      Cell: renderCategoryDescriptionCell,
    }),
    // Fee or Expense, read with the name and description it qualifies (#6882).
    columnHelper.accessor((row) => getCategoryTypeValue(row.category), {
      header: "Type",
      id: "type",
      meta: { kind: READONLY_FEE_SCHEDULE_COLUMN_KINDS.type },
      enableSorting: true,
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: "includesString",
      Cell: renderCategoryTypeCell,
    }),
    columnHelper.accessor((row) => getCategoryFixedFeeValue(row.category), {
      header: "Fixed?",
      id: "fixedFee",
      meta: { kind: READONLY_FEE_SCHEDULE_COLUMN_KINDS.fixedFee },
      enableSorting: true,
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: "includesString",
    }),
    columnHelper.accessor(
      (row) => getCategoryFeeValue(row.category, session.context?.viewingAsVendor),
      {
        header: "Fee",
        id: "fee",
        meta: { kind: READONLY_FEE_SCHEDULE_COLUMN_KINDS.fee },
        enableSorting: true,
        enableColumnFilter: true,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: (cell) => renderCategoryFeeCell(cell, session.context?.viewingAsVendor),
      }
    ),
    columnHelper.accessor((row) => getCategoryUnitValue(row.category), {
      header: "Per",
      id: "unit",
      meta: { kind: READONLY_FEE_SCHEDULE_COLUMN_KINDS.unit },
      enableSorting: true,
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: "includesString",
      Cell: renderCategoryUnitCell,
    }),
    columnHelper.accessor((row) => row.teamMemberText, {
      header: "Team Member",
      id: "teamMember",
      meta: { kind: READONLY_FEE_SCHEDULE_COLUMN_KINDS.teamMember },
      enableSorting: true,
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: "includesString",
      Cell: ({ row, table }) => {
        const { teamMember, teamMemberIsMasked } = row.original.category;
        return (
          <FeeScheduleTeamMemberCell
            teamMember={teamMember}
            teamMemberIsMasked={teamMemberIsMasked}
            individual={teamMember ? teamMemberIndividuals.get(teamMember.userId.value) : undefined}
            departure={row.original.departure}
            // The accessor's words are split here between the name's link and how they left, so the
            // name is marked on its own (#6590).
            renderName={(name) => <SearchMatch text={name} table={table} />}
          />
        );
      },
    }),
  ], [session.context?.viewingAsVendor, teamMemberIndividuals]);

  const resetToFirstPage = useCallback(() => {
    setPagination((previous) => ({ ...previous, pageIndex: 0 }));
  }, []);

  const handleColumnFiltersChange = useCallback((updater: MRT_Updater<ColumnFiltersState>) => {
    resetToFirstPage();
    setColumnFilters(updater);
  }, [resetToFirstPage]);

  const handleGlobalFilterChange = useCallback((updater: MRT_Updater<string>) => {
    resetToFirstPage();
    setGlobalFilter(updater);
  }, [resetToFirstPage]);

  const handleSortingChange = useCallback((updater: MRT_Updater<SortingState>) => {
    resetToFirstPage();
    setSorting(updater);
  }, [resetToFirstPage]);

  const renderTopToolbarCustomActions = useCallback(
    (toolbarProps: { table: MRT_TableInstance<ReadonlyFeeScheduleRow> }) => (
      <ToolbarRow>
        <GlobalFilterTextField table={toolbarProps.table} />
        <ToolbarButtons>
          <TableToolsMenu table={toolbarProps.table} />
        </ToolbarButtons>
      </ToolbarRow>
    ),
    [],
  );
  
  const tableOptions: MRT_TableOptions<ReadonlyFeeScheduleRow> = {
    columns,
    data: rows,
    // Read-only by construction; the editable twin is `fee-schedule-templates-table`.
    enableRowActions: false,
    enableRowSelection: false,
    enableTableHead: true,
    getRowId: (row) => row.category.id?.value ?? Guid.generate().value,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
    },
    enableColumnPinning: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableGlobalFilterRankedResults: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    globalFilterFn: "includesString",
    manualFiltering: false,
    manualPagination: false,
    manualSorting: false,
    muiTableBodyRowProps: ({ row }) => ({
      "data-departed-rate": row.original.departure?.label,
      sx: {
        pointerEvents: "none",
      },
    }),
    muiTableBodyCellProps: ({ cell, row }) => ({
      sx: (theme) => ({
        ...feeScheduleTableCellStyles(theme, { columnId: cell.column.id }),
        // The whole category reads as departed; the Team Member cell draws its own line (#6096).
        ...(row.original.departure && cell.column.id !== "teamMember" && departedRateCellStyles(theme)),
      }),
    }),
    muiSearchTextFieldProps: {
      placeholder: "Search",
      slotProps: {
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: globalFilter ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="Clear search"
                size="small"
                onClick={() => setGlobalFilter("")}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      },
    },
    enableToolbarInternalActions: false,
    renderTopToolbarCustomActions,
    muiTableContainerProps: {
      sx: {
        flex: 1,
        minWidth: 0,
        position: "relative",
      },
    },
    muiTablePaperProps: {
      ...paneStretchTable,
      elevation: 0,
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
      },
    },
    muiTopToolbarProps: {
      sx: {
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
        "& > .MuiBox-root": {
          gap: 0,
          minWidth: 0,
          overflow: "hidden",
        },
      },
    },
    onColumnFiltersChange: handleColumnFiltersChange,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: handleGlobalFilterChange,
    onPaginationChange: setPagination,
    onSortingChange: handleSortingChange,
    positionGlobalFilter: "none",
    renderEmptyRowsFallback: emptyRowsFallback({
      subject: "fee schedule categories",
    }),
    state: {
      columnFilters,
      columnVisibility,
      globalFilter,
      pagination,
      showGlobalFilter: true,
      sorting,
    },
  }

  return (
    <div className={className}>
      <MRTNoMemo<ReadonlyFeeScheduleRow> options={tableOptions} />
    </div>
  );
}

/** What search and sorting read for the Team Member column. The cell draws the same words, plus its controls. */
function describeTeamMember(
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  individuals: ReadonlyMap<string, Individual>,
  departure: FeeScheduleTeamMemberDeparture | undefined,
): string {
  if (category.teamMemberIsMasked) return proposalFeeScheduleRestrictedTeamMemberText;
  if (!category.teamMember) return "";
  const name = individuals.get(category.teamMember.userId.value)?.fullName || proposalFeeScheduleTeamMemberFallbackText;
  return departure ? `${name} ${departure.status}` : name;
}

function getCategoryNameValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  return category.name.value;
}

function getCategoryDescriptionValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  return category.description?.value ?? "";
}

function getCategoryFixedFeeValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  if (category instanceof FeeScheduleCategoryMaskedCurrency) return "Masked";
  return category?.fee?.unit ? "No" : "Yes";
}

function getCategoryFeeValue(
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  viewingAsVendor?: boolean,
): string {
  if (category instanceof FeeScheduleCategoryMaskedCurrency) return "Masked";
  if (!category.fee) {
    const deferredTo = viewingAsVendor === true ? "client" : "vendor";
    return `Deferred to ${deferredTo}`;
  }

  return new Intl.NumberFormat(navigator.language, {
    style: "currency",
    currency: category.fee?.rate?.currency.toString() ?? "USD",
  }).format(category.fee?.rate?.amount ?? 0);
}

function getCategoryUnitValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  if (category instanceof FeeScheduleCategoryMaskedCurrency) return "Masked";
  return category.fee?.unit ?? "";
}

function getCategoryTypeValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  return feeScheduleCategoryTypeLabel(category.type);
}
